import { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { useRouter } from 'next/router';

import { Flags } from '@watershed/constants/flags';
import FriendsIcon from '@watershed/icons/components/Friends';
import GearIcon from '@watershed/icons/components/Gear';
import LanguageIcon from '@watershed/icons/components/Language';
import ListIcon from '@watershed/icons/components/List';
import LogOutIcon from '@watershed/icons/components/LogOut';
import RainbowIcon from '@watershed/icons/components/Rainbow';
import useLogout from '@watershed/shared-frontend/hooks/useLogout';
import {
  routeForAuditLogs,
  routeForDemoPlatformIndex,
  routeForOrganizationUsers,
  routeForUserProfile,
} from '@watershed/shared-universal/dashboardRoutes';
import { SIDEBAR_EXPANDED_WIDTH } from '@watershed/shared-universal/utils/constants';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import Button from '@watershed/ui-core/components/Button';
import UnstyledLink from '@watershed/ui-core/components/UnstyledLink';
import useToggle from '@watershed/ui-core/hooks/useToggle';

import { useFeatureFlag } from '../../utils/FeatureFlag';
import { useUserContext } from '../../utils/UserContext';
import { useNavigateWithTransition } from '../../utils/useNavigateWithTransition';
import useBackToAppFromSettings from '../settings/hooks/useBackToAppFromSettings';
import NavHelpButton from '../support/NavHelpButton';
import { LocaleDialog } from './LocaleDialog';
import SwitchOrganizationDialog from './SwitchOrgDialog';
import { getSharedSidebarStyles } from './variants/sharedSidebarStyles';

export default function UserBar({
  isOnFinancePage,
  appearsCollapsed,
  isAdmin,
  isWatershedEmployee,
}: {
  isOnFinancePage: boolean;
  appearsCollapsed: boolean;
  isAdmin: boolean;
  isWatershedEmployee: boolean;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const userContext = useUserContext();
  const router = useRouter();
  const { navigate } = useNavigateWithTransition();
  const { setLastAppRoute } = useBackToAppFromSettings();
  const onLogout = useLogout();
  const [switchOrgDialogOpen, toggleSwitchOrgDialog] = useToggle();
  const [localeDialogOpen, setLocaleDialogOpen] = useState<boolean>(false);
  const localeEnabled = useFeatureFlag(Flags.LocalizationDynamicStrings);
  const mfaEnabled = useFeatureFlag(Flags.EntFoundEnableMfa);

  const theme = useTheme();
  const isLightTheme = useFeatureFlag(Flags.LightThemeSideNavigation);
  const sharedSidebarStyles = getSharedSidebarStyles(theme, isLightTheme);
  const { COLORS, BOX_SHADOW_FOCUS } = sharedSidebarStyles;

  return (
    <Box
      sx={{
        margin: 'auto 0 0',
        backgroundColor: COLORS.BACKGROUND,
        color: COLORS.TEXT_PRIMARY,
        padding: 1,
        borderTop: `0.5px solid ${COLORS.BORDER}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
      }}
    >
      <NavHelpButton
        isOnFinancePage={isOnFinancePage}
        appearsCollapsed={appearsCollapsed}
      />
      <Button
        data-testid={TestIds.UserBarButton}
        variant="text"
        startIcon={
          <GearIcon
            color={COLORS.TEXT_PRIMARY}
            style={{
              transform: anchorEl === null ? undefined : 'rotate(-30deg)',
            }}
          />
        }
        aria-controls="user-menu"
        aria-haspopup="true"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          minWidth: 'unset',
          width: '100%',
          justifyContent: 'flex-start',
          textAlign: 'left',
          color: COLORS.TEXT_PRIMARY,
          height: 'auto', // Undo default 32px height
          borderRadius: 1,
          paddingInline: 1, // Match alignment of navbar icon
          paddingBlock: 1,
          gap: 1,

          '&:hover, &:focus': {
            backgroundColor: COLORS.BACKGROUND_ACTIVE,
          },
          '&:focus-visible': {
            boxShadow: `inset ${BOX_SHADOW_FOCUS}`,
          },

          '& .MuiButton-startIcon': {
            margin: 0,
            '& svg': {
              width: 16,
              height: 16,
              transition: (theme) => theme.transitions.create('transform'),
            },
            pb: 2,
          },
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            '[data-appears-collapsed="true"] &': {
              opacity: 0,
            },
          }}
        >
          {userContext.userName}
          <br />
          <Typography variant="body3" sx={{ color: 'inherit' }}>
            {userContext.orgName}
          </Typography>
        </Box>
      </Button>
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        // Switched to anchorPosition to avoid the menu rendering off the screen
        // when the user opens the menu via keyboard (as it doesn't pass the correct
        // anchorEl to the menu, as it's triggered on click).
        anchorReference="anchorPosition"
        anchorPosition={{
          // Sidebar width -8px to account for 16px margin of the Menu applied via the
          // menuPaperSxProps.
          left: SIDEBAR_EXPANDED_WIDTH - 8,
          top: window.innerHeight,
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: sharedSidebarStyles.menuPaperSxProps,
          'data-appears-collapsed': appearsCollapsed,
        }}
      >
        {isWatershedEmployee && (
          <Box>
            <Typography
              variant="body2"
              sx={{ padding: 1, color: COLORS.TEXT_SECONDARY }}
            >
              <Trans context="Menu group heading">Watershed team only:</Trans>
            </Typography>
            <UnstyledLink href={routeForDemoPlatformIndex()}>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                }}
              >
                <RainbowIcon flexShrink={0} />
                <Trans context="Navbar menu item">Demo Platform</Trans>
              </MenuItem>
            </UnstyledLink>
            <Divider sx={{ borderColor: COLORS.BORDER }} />
          </Box>
        )}
        {(isAdmin || mfaEnabled) && (
          <MenuItem
            data-testid={TestIds.OrganizationSettingsItem}
            onClick={() => {
              setLastAppRoute(router.asPath);
              setAnchorEl(null);
              void navigate(
                mfaEnabled ? routeForUserProfile() : routeForOrganizationUsers()
              );
            }}
          >
            <GearIcon flexShrink={0} />
            <Trans context="Navbar item for user and organization settings">
              Settings
            </Trans>
          </MenuItem>
        )}

        {isAdmin && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              void navigate(routeForAuditLogs());
            }}
          >
            <ListIcon flexShrink={0} />
            <Trans context="Navbar menu item for Audit log">Audit log</Trans>
          </MenuItem>
        )}
        {userContext.accessibleOrgs.length > 1 && (
          <Tooltip
            title={
              userContext.loginAsUser !== null
                ? 'You cannot switch organizations while logged in as another user'
                : ''
            }
            placement="right"
          >
            <span>
              <MenuItem
                onClick={() => {
                  toggleSwitchOrgDialog();
                  setAnchorEl(null);
                }}
                disabled={userContext.loginAsUser !== null}
              >
                <FriendsIcon flexShrink={0} />
                <Trans context="Navbar menu item for Switch organization">
                  Switch organization
                </Trans>
              </MenuItem>
            </span>
          </Tooltip>
        )}
        {localeEnabled && (
          <MenuItem
            onClick={() => {
              setLocaleDialogOpen(true);
              setAnchorEl(null);
            }}
          >
            <LanguageIcon flexShrink={0} />
            <Trans context="The text of the button to open a dialog to switch the dashboard language">
              Change locale
            </Trans>
          </MenuItem>
        )}
        <MenuItem
          component="button"
          onClick={() => {
            void onLogout();
            setAnchorEl(null);
          }}
          sx={{
            letterSpacing: 'inherit',
            width: '100% !important',
          }}
          data-testid={TestIds.LogoutButton}
        >
          <LogOutIcon flexShrink={0} />
          <Trans context="Navbar menu item for Sign out">Sign out…</Trans>
        </MenuItem>
      </Menu>
      <SwitchOrganizationDialog
        open={switchOrgDialogOpen}
        onClose={toggleSwitchOrgDialog}
      />
      <LocaleDialog
        open={localeDialogOpen}
        onClose={() => setLocaleDialogOpen(false)}
      />
    </Box>
  );
}
